import { useDynamicEnums } from '@spx-portal/shared-hooks';
import { isAdmin } from '@spx-portal/shared-utils';
import React from 'react';
import { InputFieldType } from 'react-pro-components/typings/components/edit-area/ctrls/input';
import type { SelectFieldType } from 'react-pro-components/typings/components/edit-area/ctrls/select';
import { TimeRangePickerFieldType } from 'react-pro-components/typings/components/edit-area/ctrls/time-range-picker';
import { ColumnProps } from 'ssc-ui-react/typings/components/table/types';

import { $gt } from '@/common/appCtx';
import Ellipsis from '@/components/Ellipsis';
import { useWorkforceEnums } from '@/store/workforce-enums';

import { TabStatus } from './const';

const shiftNameColumn = {
  get title() { return $gt('Shift Name'); },
  dataIndex: 'slot_code',
  key: 'slot_code',
  width: 150,
  render: (val: string) => {
    return (
      <Ellipsis
        tooltip
        style={{ width: 120 }}
      >
        {val}
      </Ellipsis>
    );
  },
};

const startTimeEndTimeColumn = {
  get title() { return $gt('Start Time - End Time'); },
  dataIndex: 'start_time',
  key: 'start_time',
  width: 180,
  render: (_: any, row: { start_time: string; end_time: string }) => {
    const { start_time = '', end_time = '' } = row;
    const [startH, startM] = start_time.split(':');
    const [h, m] = end_time.split(':');
    let hour = h;
    let crossDay = '';
    if (Number(h) >= 24) {
      hour = (Number(h) % 24).toString();
      crossDay = `(+${Math.floor(Number(h) / 24)})`;
    }
    const value = `${startH}:${startM}-${hour}:${m}${crossDay}`;
    return (
      <Ellipsis
        tooltip
        style={{ width: 120 }}
       >
        {value}
      </Ellipsis>
    );
  },
};

const activeStationColumn: ColumnProps<any> = {
  get title() { return $gt('Active Station'); },
  dataIndex: 'active_station_list	',
  key: 'active_station_list	',
  hidden: !isAdmin(),
  width: 150,
  hint: $gt('Show station ID of stations using this shift for pending, ongoing and pending ongoing events'),
  render: (_: any, row: { active_station_list?: Array< { station_id: number; station_name: string }> }) => {
    return (
      <Ellipsis
        tooltip
        style={{ width: 120 }}
      >
        {row.active_station_list?.map((item) => `[${item.station_id}] ${item.station_name}`).join(',') || '-'}
      </Ellipsis>
    );
  },
};

type UseFieldsType = {
  lineClamp?: number;
  activeTab?: string;
}

export const useFields = (props?: UseFieldsType) => {
  const { options: tripStatusOption, mapping: tripStatusMap } = useDynamicEnums({ path: 'trip_status' });
  const enums = useWorkforceEnums.use.enums();
  const staffTypeOption = React.useMemo(() => {
    if (!enums || !enums['RosterPlanningSlotType']) return [];
    return Object.keys(enums['RosterPlanningSlotType']).map((key) => {

      return {
        label: key,
        value: enums['RosterPlanningSlotType'][key],
      };
    });
  }, [enums]);
  const staffTypeMap = React.useMemo(() => {
    return Object.fromEntries(staffTypeOption.map((item) => [item.value, item.label]));
  }, [staffTypeOption]);

  const staffTypeColumn = {
    get title() { return $gt('Staff Type'); },
    dataIndex: 'staff_type',
    key: 'staff_type',
    width: 150,
    render: (val: number) => {
      return (
        <Ellipsis
          tooltip
          style={{ width: 120 }}
        >
          {staffTypeMap[val] || '-'}
        </Ellipsis>
      );
    },
  };

  const usingEventColumn = {
    get title() { return $gt('Active Event'); },
    dataIndex: 'event_id_list',
    key: 'event_id_list',
    hidden: isAdmin() || props?.activeTab === TabStatus.Pending,
    width: 150,
    render: (val: string[]) => {
      return (
        <Ellipsis
          tooltip
          style={{ width: 150 }}
        >
          {val?.join(',') || '-'}
        </Ellipsis>
      );
    },
  };

  const shiftNameField: InputFieldType = {
    get label() { return $gt('Shift Name'); },
    name: 'slot_code',
    type: 'input',
    ctrlProps: {
      allowClear: true,
    },
  };

  const startTimeEndTimeField: TimeRangePickerFieldType = {
    get label() { return $gt('Start Time - End Time'); },
    // notice that it will transform to start_time and end_time
    name: 'slot_time',
    type: 'timerangepicker',
    ctrlProps: {
      format: 'HH:mm',
      includeMidnight: true,
      showConfirm: true,
      allowClear: true,
    },
  };

  const crossDayField: InputFieldType = {
    get label() { return $gt('Cross Day'); },
    name: 'cross_day',
    type: 'input',
    ctrlProps: {
      allowClear: true,
    },
  };

  const staffTypeField: SelectFieldType = {
    get label() { return $gt('Staff Type'); },
    name: 'staff_type',
    type: 'select',
    ctrlProps: {
      options: staffTypeOption,
      allowClear: true,
    },
  };

  return {
    staffTypeOption,
    queryField: {
      shiftNameField,
      slotTimeField: startTimeEndTimeField,
      crossDayField,
      staffTypeField,
    },
    showColumn: {
      shiftNameColumn,
      startTimeEndTimeColumn,
      staffTypeColumn,
      usingEventColumn,
      activeStationColumn,
    },
  };
};
